import dynamic from 'next/dynamic';
import React, { type ComponentProps, useEffect, useMemo, useState } from 'react';
import type { Document as DocumentType, Page as PageType } from 'react-pdf';

import { CMapReaderFactory } from '~/utils/pdfjs_cmap_reader_factory';

type DocumentProps = ComponentProps<typeof DocumentType>;

const defaultOptions: DocumentProps['options'] = { CMapReaderFactory };

// pdfjs-dist v5 のモダンビルドは Node 上で評価できないためクライアントだけで動的 import する。
// Document はチャンク取得中も呼び出し元の `loading` を描画したいので状態を自前で扱う
// (next/dynamic の `loading` option では親の prop を流せない)。
let reactPdfModulePromise: Promise<typeof import('react-pdf')> | null = null;
const loadReactPdf = () => {
  if (!reactPdfModulePromise) {
    reactPdfModulePromise = import('react-pdf').then((mod) => {
      mod.pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();
      return mod;
    });
  }
  return reactPdfModulePromise;
};

export const Document = ({ loading, children, options, ...rest }: DocumentProps) => {
  const [Resolved, setResolved] = useState<typeof DocumentType | null>(null);
  // react-pdf は options の参照が変わるたびに PDF を再ロードするので、毎回組み立てない。
  // 呼び出し側も options は安定した参照で渡すこと（インラインのオブジェクトだと毎レンダー再ロードになる）。
  const mergedOptions = useMemo(
    () => (options ? { ...defaultOptions, ...options } : defaultOptions),
    [options],
  );

  useEffect(() => {
    let cancelled = false;
    loadReactPdf().then((mod) => {
      if (!cancelled) setResolved(() => mod.Document);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!Resolved) return <>{loading ?? null}</>;

  return (
    <Resolved loading={loading} options={mergedOptions} {...rest}>
      {children}
    </Resolved>
  );
};

export const Page = dynamic(() => loadReactPdf().then((m) => ({ default: m.Page })), {
  ssr: false,
}) as unknown as typeof PageType;
